/**
 * The HTTP boundary for the tenant app.
 *
 * Everything the screens do goes through the generated typed client (CLAUDE.md
 * §7); this module owns the two cross-cutting concerns the generator cannot
 * know about:
 *
 *  - **Token lifecycle.** The access token lives in memory only (15 minutes,
 *    §6). The refresh token is persisted so a reload does not sign the user out,
 *    and rotation is handled here so no screen ever thinks about it.
 *  - **Single-flight refresh.** Ten widgets mount at once, ten requests 401 at
 *    once — that must produce exactly one `POST /auth/refresh`, not ten. A
 *    burst of refreshes would race the server's rotation-with-reuse-detection
 *    and log the user out for a crime they did not commit.
 *  - **Step-up challenges.** `403 ERR_STEP_UP_REQUIRED` (§6/§13) is not a
 *    failure to report, it is a question to answer: prompt for a one-time code,
 *    swap the access token, replay the request. Doing it here is what makes it
 *    true of every guarded action rather than of whichever screen remembered.
 *
 * Interception happens by subclassing `ApiClient` rather than by wrapping
 * `fetch`. `ApiClient.request` reads the token through a getter on every call,
 * so a retry after a refresh picks up the new token for free, and no code here
 * has to reason about `Headers`, streams or response cloning.
 */

import {
  ApiClient,
  authRefresh,
  authRevoke,
  authStepUp,
  type LoginResult,
  type RequestInit_,
} from '@erpx/api'
import { isProblemDetails, toProblemDetails, type ProblemDetails } from '@erpx/ui'
import { ref, type Ref } from 'vue'

import { stepUp, stepUpReason } from '@/api/stepup'

/** Stable machine codes we branch on that are not in the shared catalogue. */
export const WEB_ERROR_CODES = {
  mfaRequired: 'ERR_MFA_REQUIRED',
  stepUpRequired: 'ERR_STEP_UP_REQUIRED',
  tenantSuspended: 'ERR_TENANT_SUSPENDED',
  sodViolation: 'ERR_SOD_VIOLATION',
  rowVersion: 'ERR_ROW_VERSION',
  passwordChangeRequired: 'ERR_PASSWORD_CHANGE_REQUIRED',
  /**
   * Client-side only — the server never sends it. What every request that
   * settles after a session boundary rejects with, success or failure alike.
   */
  sessionEnded: 'ERR_SESSION_ENDED',
} as const

/**
 * The one error a request that outlived its session settles with.
 *
 * Deliberately empty of anything the request produced: no server `detail`, no
 * field errors, no trace id, no data. A response that arrives after a sign-out
 * belongs to the person who left, and anything it carried — a record name in a
 * success toast, the server's sentence in a danger toast that never times out,
 * a record URL a save handler navigates to — would otherwise be rendered for
 * whoever is at the device next. The title is a fixed sentence, safe on any
 * screen; `problemText` falls back to it until the locale catalogue has an
 * `errors.codes.ERR_SESSION_ENDED` entry. A fresh object per throw, so a caller
 * that decorates the error cannot leak into another caller's.
 */
export function sessionEndedProblem(): ProblemDetails {
  return {
    type: 'about:blank',
    title: 'Your session has ended.',
    status: 401,
    code: WEB_ERROR_CODES.sessionEnded,
  }
}

/** Whether a thrown value is `sessionEndedProblem()`. */
export function isSessionEndedError(error: unknown): boolean {
  return toProblemDetails(error)?.code === WEB_ERROR_CODES.sessionEnded
}

const REFRESH_TOKEN_KEY = 'erpx.web.refreshToken'
const TENANT_SLUG_KEY = 'erpx.web.tenantSlug'

/**
 * Paths that must never trigger a refresh-and-retry (they *are* the refresh).
 *
 * `/auth/step-up` is on the list for a sharper reason than the others: a code
 * it refuses comes back as 401, and a blind retry would re-send the same
 * one-time code — burning a second attempt from the lockout budget the server
 * shares with login, for a code the server has already spent.
 */
const AUTH_PATHS = [
  '/auth/login',
  '/auth/refresh',
  '/auth/revoke',
  '/auth/accept-invite',
  '/auth/reset-password',
  '/auth/forgot-password',
  '/auth/step-up',
]

/* ------------------------------------------------------------------- state */

const accessToken = ref<string | null>(null)
const accessTokenExpiresAt = ref<string | null>(null)

/**
 * Which session the tab is in. Bumped by every boundary — `clearSession()` (a
 * sign-out, a refused refresh, the start of a new sign-in) and `applySession()`
 * (a new token pair adopted from a login or an invitation).
 *
 * Anything that awaits the network and then writes or uses a token captures
 * the generation before the await and compares after it. A refresh that was in
 * flight when the user signed out would otherwise write its ROTATED refresh
 * token back to storage — and the next reload would resume the previous person
 * — and a request that 401'd under one person would be replayed under the next
 * person's token.
 */
let sessionGeneration = 0

/**
 * The generation of the session the tab is in now. `stores/auth.ts` captures it
 * around its own awaits (a sign-in that answers after another boundary passed
 * must not be applied).
 */
export function currentApiGeneration(): number {
  return sessionGeneration
}

/* --------------------------------------------- which session this tab holds */

/**
 * The refresh token lives in localStorage, which every tab of the origin
 * shares. So the stored token is not "this tab's session" — it is whichever
 * session was written last, by any tab. Without a per-tab record of which one
 * the tab actually adopted, a tab still showing A's screens would, on its next
 * 401, rotate the token B just signed in with in another tab and carry on with
 * B's access token under A's identity, permissions and cache — and its "Sign
 * out" would then revoke B's session.
 *
 * So each tab remembers the SESSION ID (the refresh token up to its last dot —
 * the server's `joinRefreshToken(sessionID, secret)`) of the session it
 * adopted, and nothing that reads the stored token — a refresh, a revoke — acts
 * on one whose session id differs.
 *
 * Three states:
 *  - `null` — this page life has not adopted a session yet. Only a cold load is
 *    here; its bootstrap refresh CLAIMS the stored session (see
 *    `performRefresh`).
 *  - a string — the adopted session's id.
 *  - `SESSION_ENDED` — the tab held a session and it ended. A tombstone, not a
 *    return to `null` (CLAUDE.md lesson 11.2): `null` would let the next stray
 *    refresh claim whoever is stored now — another tab's, another person's —
 *    and that must take a sign-in in this tab.
 */
const SESSION_ENDED: unique symbol = Symbol('erpx.session-ended')
let adoptedSessionId: string | typeof SESSION_ENDED | null = null

/**
 * This tab's own copy of its session's refresh token, kept in step with
 * storage while storage holds this session. It is what lets the tab revoke its
 * OWN session once another tab has overwritten the stored token with someone
 * else's — otherwise that session would outlive every tab that could reach it,
 * alive on the server for 30 days. Same exposure as the stored copy it
 * mirrors.
 */
let adoptedRefreshToken: string | null = null

/**
 * The session a refresh token belongs to. The server's session id contains
 * dots and the secret (unpadded base64url) never does, so the last dot is the
 * boundary — exactly `splitRefreshToken` in `server/internal/core/auth`.
 *
 * A token that cannot be split (never, from the real server) is treated as a
 * session of its own: it then matches only itself, which fails closed.
 */
function sessionIdOf(token: string): string {
  const dot = token.lastIndexOf('.')
  if (dot <= 0 || dot === token.length - 1) return token
  return token.slice(0, dot)
}

/** Whether `token` belongs to the session this tab adopted. */
function isOwnSession(token: string): boolean {
  return typeof adoptedSessionId === 'string' && sessionIdOf(token) === adoptedSessionId
}

/** The refresh every concurrent 401 joins (see `refreshAccessToken`). */
let inflightRefresh: Promise<boolean> | null = null

/**
 * Set when the API answers `ERR_TENANT_SUSPENDED`. The shell reads it to raise
 * a persistent banner and put every write affordance into read-only mode (§10).
 */
export const tenantSuspended = ref(false)

/** Raised when the refresh token is gone or rejected; the router signs out. */
export const sessionExpired = ref(false)

/**
 * Set when the API answers `ERR_PASSWORD_CHANGE_REQUIRED` (§6): the caller is
 * still carrying a password somebody else chose for them, and the server refuses
 * every tenant route outside the `auth.*` family until they change it.
 *
 * The router's guard already covers navigation — it reads the flag off
 * `/auth/me`. This ref is the backstop for the other half: a page that was
 * already open when the state began, whose next request comes back 403. Without
 * it a screen would render a refusal the user cannot act on from where they are
 * standing.
 *
 * It lives here, as a ref, rather than as a `router.push` from the transport,
 * for the same reason `api/stepup.ts` keeps the arrows one-way: the router
 * imports the client, so the client importing the router would close the cycle.
 * `createAppRouter` watches this and navigates.
 */
export const passwordChangeRequired = ref(false)

/** Clear the backstop once the user has actually changed their password. */
export function clearPasswordChangeRequired(): void {
  passwordChangeRequired.value = false
}

export const tenantSlug: Ref<string | null> = ref(readStored(TENANT_SLUG_KEY))

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    // Private mode or a blocked storage partition: degrade to session-only auth.
    return null
  }
}

function writeStored(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    // Nothing to do — the in-memory session still works for this tab.
  }
}

/* ----------------------------------------------------------------- clients */

function defaultBaseUrl(): string {
  const configured = import.meta.env.VITE_API_BASE_URL
  if (configured !== undefined && configured !== '') return configured
  return typeof window === 'undefined' ? 'http://localhost' : window.location.origin
}

interface ApiConfig {
  baseUrl: string
  fetchImpl: typeof fetch
}

let config: ApiConfig = {
  baseUrl: defaultBaseUrl(),
  fetchImpl: (...args: Parameters<typeof fetch>) => globalThis.fetch(...args),
}

function isAuthPath(path: string): boolean {
  return AUTH_PATHS.some((candidate) => path.includes(candidate))
}

/**
 * The client every screen uses: it refreshes once on 401 and retries, it
 * answers a step-up challenge and replays the request behind it, and it notices
 * a suspended tenant on the way past.
 */
class AuthedClient extends ApiClient {
  /**
   * Every request settles inside the session it was made for, or not at all.
   *
   * When the session changed between the call and the answer, the caller gets
   * `sessionEndedProblem()` — for a success as much as for a failure. Handing
   * back the success would let the previous person's record reach a toast or a
   * navigation on the next person's screen; rethrowing the server's error would
   * put the server's sentence about the previous person's record in a danger
   * toast that never times out.
   */
  override async request<TResponse>(
    method: string,
    path: string,
    init?: RequestInit_,
  ): Promise<TResponse> {
    const generation = sessionGeneration
    let value: TResponse
    try {
      value = await this.attempt<TResponse>(generation, method, path, init)
    } catch (error) {
      if (generation !== sessionGeneration) throw sessionEndedProblem()
      throw error
    }
    if (generation !== sessionGeneration) throw sessionEndedProblem()
    return value
  }

  private async attempt<TResponse>(
    generation: number,
    method: string,
    path: string,
    init: RequestInit_ | undefined,
  ): Promise<TResponse> {
    // `generation` is the session this request was made for. A 401 or a
    // step-up challenge that comes back after the session changed is not this
    // tab's to answer: a replay would send the previous person's request under
    // the next person's token.
    try {
      return await super.request<TResponse>(method, path, init)
    } catch (error) {
      if (generation !== sessionGeneration) throw error
      const problem = toProblemDetails(error)

      if (problem?.code === WEB_ERROR_CODES.tenantSuspended) {
        tenantSuspended.value = true
        throw error
      }

      // Not a question the transport can answer on the user's behalf, unlike a
      // step-up: choosing a password is a screen, not a one-time code. So the
      // flag is raised and the error still propagates — the router navigates,
      // and whatever the caller does with the rejection happens on a page that
      // is on its way out.
      if (problem?.code === WEB_ERROR_CODES.passwordChangeRequired) {
        passwordChangeRequired.value = true
        throw error
      }

      if (problem?.code === WEB_ERROR_CODES.stepUpRequired && !isAuthPath(path)) {
        // The user proves who they are, and then the action they pressed the
        // button for actually happens. `init` is reused verbatim, including any
        // Idempotency-Key: the step-up guard runs before the idempotency
        // middleware, so the refused attempt recorded nothing to replay.
        const outcome = await stepUp.challenge(stepUpReason(path))
        if (outcome !== 'granted' || generation !== sessionGeneration) throw error
        return super.request<TResponse>(method, path, init)
      }

      const retryable = problem?.status === 401 && !isAuthPath(path) && accessToken.value !== null
      if (!retryable) throw error

      const refreshed = await refreshAccessToken()
      if (!refreshed || generation !== sessionGeneration) throw error

      return super.request<TResponse>(method, path, init)
    }
  }
}

/** The refresh call itself must not be intercepted, or it would recurse. */
let plainClient = new ApiClient({
  baseUrl: config.baseUrl,
  getToken: () => accessToken.value,
  fetch: config.fetchImpl,
})

let authedClient: ApiClient = new AuthedClient({
  baseUrl: config.baseUrl,
  getToken: () => accessToken.value,
  fetch: config.fetchImpl,
})

/**
 * Sends no Authorization header at all, ever. Used for `POST /auth/revoke`,
 * whose credential is the refresh token in the body: a revoke of an orphaned
 * token can run while the NEXT person's session is live in this tab, and their
 * access token has no business riding along on it.
 */
let anonymousClient = new ApiClient({
  baseUrl: config.baseUrl,
  getToken: () => null,
  fetch: config.fetchImpl,
})

function rebuildClients(): void {
  plainClient = new ApiClient({
    baseUrl: config.baseUrl,
    getToken: () => accessToken.value,
    fetch: config.fetchImpl,
  })
  authedClient = new AuthedClient({
    baseUrl: config.baseUrl,
    getToken: () => accessToken.value,
    fetch: config.fetchImpl,
  })
  anonymousClient = new ApiClient({
    baseUrl: config.baseUrl,
    getToken: () => null,
    fetch: config.fetchImpl,
  })
}

/**
 * Point the app at a different origin or transport. `main.ts` never calls this
 * (the defaults are right); tests do, to drive the client against a fake fetch
 * instead of a live server.
 */
export function configureApi(next: Partial<ApiConfig>): void {
  config = { ...config, ...next }
  rebuildClients()
}

/** The client screens should use. Always read it through this function. */
export function api(): ApiClient {
  return authedClient
}

/** The un-intercepted client, for the auth endpoints themselves. */
export function publicApi(): ApiClient {
  return plainClient
}

/* --------------------------------------------------------------- raw responses */

/**
 * Issue an authenticated request and hand back the untouched `Response`.
 *
 * `ApiClient.request` parses a JSON body and throws the rest away, which is the
 * right default for 69 of the API's 70 operations. `GET /audit/export` is the
 * exception: its body is a CSV file and its payload — the evidence attestation
 * an external auditor recomputes the chain from — travels in response headers
 * (`X-Erpx-Audit-Row-Count`, `X-Erpx-Audit-Chain-Hash`, `X-Erpx-Audit-First-Id`,
 * `X-Erpx-Audit-Last-Id`). Headers are unreachable from a plain anchor download
 * and unreachable from the generated client, so this is the seam.
 *
 * It keeps the behaviours that make `AuthedClient` safe — one refresh-and-
 * retry on 401, noticing a suspended tenant, and settling as
 * `sessionEndedProblem()` once the session it was made for has ended — and
 * throws the same problem+json object every screen already knows how to render.
 *
 * `body` takes a `FormData` for the hand-assembled multipart uploads, which
 * otherwise have no seam that keeps those guarantees.
 *
 * Note for a cross-origin deployment: the browser only reveals non-safelisted
 * response headers that the server names in `Access-Control-Expose-Headers`.
 * The apps are served same-origin behind the ingress (and same-origin through
 * Vite's dev proxy), so this holds today; callers still treat a missing
 * attestation as "not returned" rather than as zero.
 */
export async function rawRequest(
  method: string,
  path: string,
  query?: Record<string, string>,
  body?: FormData,
): Promise<Response> {
  // Same rule as `AuthedClient`: never replay across a session boundary, and
  // whatever settles after one — a response, a refusal, a network error —
  // settles as `sessionEndedProblem()`.
  const generation = sessionGeneration
  try {
    return await rawAttempt(generation, method, path, query, body)
  } catch (error) {
    if (generation !== sessionGeneration) throw sessionEndedProblem()
    throw error
  }
}

async function rawAttempt(
  generation: number,
  method: string,
  path: string,
  query: Record<string, string> | undefined,
  body: FormData | undefined,
): Promise<Response> {
  let response = await sendRaw(method, path, query, body)

  const retryable =
    response.status === 401 &&
    !isAuthPath(path) &&
    accessToken.value !== null &&
    generation === sessionGeneration
  if (retryable && (await refreshAccessToken()) && generation === sessionGeneration) {
    response = await sendRaw(method, path, query, body)
  }

  // The same step-up courtesy the typed client gets. No route reached through
  // here is guarded today, but "guarded actions can be answered" is a property
  // of the transport, not of the one operation that happens to need it.
  if (
    response.status === 403 &&
    !isAuthPath(path) &&
    generation === sessionGeneration &&
    (await isStepUpRefusal(response))
  ) {
    if (
      (await stepUp.challenge(stepUpReason(path))) === 'granted' &&
      generation === sessionGeneration
    ) {
      response = await sendRaw(method, path, query, body)
    }
  }

  if (generation !== sessionGeneration) {
    // The answer belongs to a session this tab no longer holds. Whatever it
    // was, the caller gets the one generic failure, not the previous person's
    // file or the server's sentence about it.
    throw sessionEndedProblem()
  }

  if (!response.ok) {
    const failure = await rawFailure(response, generation)
    // Reading the body is itself an await a boundary can pass during.
    if (generation !== sessionGeneration) throw sessionEndedProblem()
    throw failure
  }
  return response
}

/** Whether a 403 is the step-up challenge, read without consuming the body. */
async function isStepUpRefusal(response: Response): Promise<boolean> {
  try {
    const body: unknown = await response.clone().json()
    return isProblemDetails(body) && body.code === WEB_ERROR_CODES.stepUpRequired
  } catch {
    return false
  }
}

async function sendRaw(
  method: string,
  path: string,
  query: Record<string, string> | undefined,
  body: FormData | undefined,
): Promise<Response> {
  const url = new URL(config.baseUrl.replace(/\/+$/, '') + path)
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== '') url.searchParams.set(key, value)
    }
  }

  const headers: Record<string, string> = {}
  const token = accessToken.value
  if (token !== null) headers['Authorization'] = `Bearer ${token}`

  // A multipart body goes as-is with no Content-Type: only the runtime knows
  // the boundary it is about to write.
  const init: RequestInit = { method, headers }
  if (body !== undefined) init.body = body
  return config.fetchImpl(url.toString(), init)
}

/** Mirror `AuthedClient`'s error shape so callers branch on one thing. */
async function rawFailure(response: Response, generation: number): Promise<unknown> {
  const body: unknown = await response.json().catch(() => undefined)
  if (isProblemDetails(body)) {
    // A refusal for a session that has since ended raises no flag on the next.
    if (generation === sessionGeneration) {
      if (body.code === WEB_ERROR_CODES.tenantSuspended) tenantSuspended.value = true
      if (body.code === WEB_ERROR_CODES.passwordChangeRequired) passwordChangeRequired.value = true
    }
    return body
  }
  return new Error(`request failed: ${response.status}`)
}

/* ------------------------------------------------------------------ session */

export function currentAccessToken(): string | null {
  return accessToken.value
}

export function accessTokenExpiry(): string | null {
  return accessTokenExpiresAt.value
}

export function hasStoredSession(): boolean {
  return readStored(REFRESH_TOKEN_KEY) !== null
}

/**
 * The refresh token of THIS TAB's session, or `null` when the tab holds none.
 * A sign-out reads it BEFORE the teardown, so the server-side session can
 * still be revoked by the one credential that outlives a 15-minute access
 * token.
 *
 * Never another tab's session: when storage holds a token of a different
 * session (somebody signed in elsewhere since this tab adopted its own), that
 * token is not this tab's to revoke — revoking it would sign out whoever is
 * using it. The tab's own copy is returned instead, so its own session still
 * dies with its sign-out. A tab that has adopted nothing (a cold tab, or one
 * whose session already ended) has nothing to revoke.
 */
export function ownRefreshToken(): string | null {
  if (typeof adoptedSessionId !== 'string') return null
  const stored = readStored(REFRESH_TOKEN_KEY)
  if (stored !== null && stored !== '' && isOwnSession(stored)) return stored
  return adoptedRefreshToken
}

/**
 * Revoke a refresh token server-side (`POST /auth/revoke`), and with it the
 * session it belongs to.
 *
 * The refresh token is the credential, carried in the body, and no
 * Authorization header is sent: this must work after the access token has
 * expired (an idle tab signed out after 15 minutes), after the teardown has
 * already dropped it, and while somebody else's session is live in the tab.
 * The server answers 204 whatever it finds.
 *
 * Never throws: every caller has already ended the session locally, and a
 * revoke that cannot reach the server leaves the token to expire on the
 * server's clock — there is nothing more the tab can do. `async` so that even
 * a synchronous throw on the way into the generated call becomes a swallowed
 * rejection rather than an exception in the caller's teardown path.
 */
export async function revokeRefreshToken(token: string | null): Promise<void> {
  if (token === null || token === '') return
  try {
    await authRevoke(anonymousClient, { refreshToken: token })
  } catch {
    // See above: the local teardown has already happened.
  }
}

/**
 * Write a token pair into the tab: memory for the access token, storage for
 * the refresh token. Shared by a new session (`applySession`) and by a refresh
 * of the current one (`performRefresh`, which has already checked that the
 * session it refreshed is still the tab's, and still the stored one). Either
 * way the tab now holds this token's session.
 */
function writeTokens(result: LoginResult): void {
  accessToken.value = result.accessToken
  accessTokenExpiresAt.value = result.expiresAt
  adoptedSessionId = sessionIdOf(result.refreshToken)
  adoptedRefreshToken = result.refreshToken
  writeStored(REFRESH_TOKEN_KEY, result.refreshToken)
  sessionExpired.value = false
  tenantSuspended.value = false
  // Stale from whoever held this tab before: the new session's own answer
  // travels on `result.user.mustChangePassword`, which the store reads.
  passwordChangeRequired.value = false
}

/**
 * Adopt the tokens from a login or invite-acceptance response.
 *
 * A new session: the generation moves on, so a refresh still in flight for
 * whoever held the tab before cannot land on top of it.
 */
export function applySession(result: LoginResult, slug?: string): void {
  sessionGeneration += 1
  inflightRefresh = null
  writeTokens(result)
  if (slug !== undefined && slug !== '') {
    tenantSlug.value = slug
    writeStored(TENANT_SLUG_KEY, slug)
  }
}

/**
 * Adopt the access token a successful step-up minted.
 *
 * Only the access token changes: `POST /auth/step-up` leaves the refresh token
 * alone (the step-up is stamped on the *session*, so the next rotation carries
 * it forward on its own) and the stored one stays valid.
 */
function applyStepUp(accessTokenValue: string, expiresAt: string): void {
  accessToken.value = accessTokenValue
  accessTokenExpiresAt.value = expiresAt
}

/**
 * Teach the broker how to answer a challenge in this realm.
 *
 * Installed here, at module load, because this is the module that owns both the
 * token state and the generated operation. `plainClient` is used deliberately:
 * `AuthedClient` would try to refresh-and-retry the 401 that a refused code
 * produces, re-spending a one-time code the server has already burned.
 */
stepUp.setVerifier(async (code: string) => {
  const generation = sessionGeneration
  const result = await authStepUp(plainClient, { code })
  if (generation !== sessionGeneration) {
    // The session that asked for the code ended while it was being checked.
    // Its stepped-up access token must not become the tab's — and a refusal,
    // not a grant, is what stops the broker replaying anything behind it.
    // (Step-up rotates no refresh token, so there is nothing to revoke.)
    throw new Error('step-up answered for a session that has ended')
  }
  applyStepUp(result.accessToken, result.expiresAt)
})

/**
 * Drop every trace of the session. Safe to call when there is none.
 *
 * Moves the generation on, and forgets the in-flight refresh: the next 401 —
 * from the next person's session — must start its own refresh rather than
 * join one that belongs to the session that just ended.
 *
 * The stored refresh token is removed only when it is this tab's own session.
 * Storage is shared by every tab: when it holds another session (another tab
 * signed in since), that session is not this tab's to end.
 */
export function clearSession(): void {
  sessionGeneration += 1
  inflightRefresh = null
  accessToken.value = null
  accessTokenExpiresAt.value = null
  const stored = readStored(REFRESH_TOKEN_KEY)
  if (stored !== null && (stored === '' || isOwnSession(stored))) {
    writeStored(REFRESH_TOKEN_KEY, null)
  }
  adoptedSessionId = SESSION_ENDED
  adoptedRefreshToken = null
  tenantSuspended.value = false
  passwordChangeRequired.value = false
  // A challenge outlives the session it belonged to only as a dialog asking for
  // a code nothing will accept.
  stepUp.reset()
}

/**
 * The session this tab holds has ended without anyone in this tab pressing
 * anything: a refused refresh, or another tab signing out or signing somebody
 * else in. The same teardown for all of them — drop the tokens and raise
 * `sessionExpired`, whose watchers (the auth store, synchronously; the router,
 * on the next flush) tear down the client state and move the tab to sign-in.
 */
function endSessionImplicitly(): void {
  clearSession()
  sessionExpired.value = true
}

/* ---------------------------------------------------- single-flight refresh */

async function performRefresh(): Promise<boolean> {
  const generation = sessionGeneration

  // A tab whose session has ended has nothing to refresh, and must not claim
  // whoever is stored now (another tab's session — possibly another person's).
  // Nothing is torn down: that already happened when the session ended.
  if (adoptedSessionId === SESSION_ENDED) return false

  const token = readStored(REFRESH_TOKEN_KEY)
  if (token === null || token === '') {
    endSessionImplicitly()
    return false
  }

  if (adoptedSessionId === null) {
    // A cold load: the stored session becomes this tab's from here on, so a
    // storage change while this refresh is in flight is judged against it.
    adoptedSessionId = sessionIdOf(token)
    adoptedRefreshToken = token
  } else if (sessionIdOf(token) !== adoptedSessionId) {
    // Another tab has since signed somebody in. Their token is not this tab's
    // to rotate — doing so would hand this tab, still showing the previous
    // person's screens, the next person's access. This tab's session is over.
    endSessionImplicitly()
    return false
  }
  const refreshing = adoptedSessionId

  let result: LoginResult
  try {
    result = await authRefresh(plainClient, { refreshToken: token })
  } catch {
    // The session this refresh was for has already ended (a sign-out, or a
    // new sign-in, passed while it was in flight). Its failure is not news,
    // and tearing down now would end the NEXT person's session.
    if (generation !== sessionGeneration) return false
    // A rejected refresh token is terminal: rotation reuse-detection has fired,
    // or the token expired. Either way the only correct move is to sign out.
    endSessionImplicitly()
    return false
  }

  if (generation !== sessionGeneration) {
    // The server rotated the previous session's refresh token after the tab
    // let go of it. Writing it back would let the next reload resume that
    // session; dropping it silently would leave it live on the server for 30
    // days. Revoke it — fire-and-forget, `revokeRefreshToken` never throws.
    void revokeRefreshToken(result.refreshToken)
    return false
  }

  // Storage events arrive as tasks, after this continuation: storage itself
  // is the truth. If another tab signed out or signed somebody else in while
  // the refresh was in flight, writing the rotated token would overwrite (or
  // resurrect) the device's session with this one.
  const stored = readStored(REFRESH_TOKEN_KEY)
  if (stored === null || stored === '' || sessionIdOf(stored) !== refreshing) {
    void revokeRefreshToken(result.refreshToken)
    endSessionImplicitly()
    return false
  }

  writeTokens(result)
  return true
}

/* ------------------------------------------------------- other tabs' changes */

/**
 * Follow what other tabs do to the shared refresh token.
 *
 *  - Removed (a sign-out, a refused refresh, or a sign-in starting over in
 *    another tab), or replaced by a DIFFERENT session's token (somebody signed
 *    in there): this tab's session is over. It ends here, synchronously, with
 *    the same teardown a refused refresh takes — the shell unmounts and the
 *    router sends the tab to sign-in. When the token was replaced, this tab's
 *    own session is revoked with its own copy of the token: no tab can reach
 *    it any more, and it must not stay live on the server for 30 days.
 *  - Replaced by the SAME session's token with a new secret (another tab of
 *    this same session refreshed): adopted silently. Tearing down here would
 *    have two tabs of one person sign each other out on every refresh.
 *
 * The cross-tab end raises `sessionExpired` like any other implicit end, so
 * the sign-in screen says the session expired. That is the boring choice: the
 * person in front of this tab may be the same one, the notice names nobody,
 * and an implicit end keeps the principal marker, so only that same person
 * signing back in here is returned to where they were.
 *
 * A tab that holds no session (a cold tab, or one whose session already
 * ended) ignores every change: it has nothing to tear down, and adopting
 * another tab's session takes a sign-in or a reload.
 */
function onStorage(event: StorageEvent): void {
  // `key === null` is a `localStorage.clear()` in another tab.
  if (event.key !== null && event.key !== REFRESH_TOKEN_KEY) return
  if (typeof adoptedSessionId !== 'string') return

  const next = event.key === null ? null : event.newValue
  if (next !== null && next !== '' && sessionIdOf(next) === adoptedSessionId) {
    adoptedRefreshToken = next
    return
  }

  const own = adoptedRefreshToken
  endSessionImplicitly()
  if (next !== null && next !== '') void revokeRefreshToken(own)
}

let storageListener: ((event: StorageEvent) => void) | null = null

function startCrossTabSync(): void {
  if (typeof window === 'undefined' || storageListener !== null) return
  storageListener = onStorage
  window.addEventListener('storage', storageListener)
}

function stopCrossTabSync(): void {
  if (typeof window === 'undefined' || storageListener === null) return
  window.removeEventListener('storage', storageListener)
  storageListener = null
}

startCrossTabSync()

// A hot-reloaded copy of this module must not leave the old copy's listener
// acting on state nothing reads any more.
if (import.meta.hot) import.meta.hot.dispose(stopCrossTabSync)

/**
 * Refresh the access token, collapsing concurrent callers onto one request.
 * Every 401 in a burst awaits the same promise, so the server sees exactly one
 * rotation.
 *
 * A session boundary forgets the in-flight promise (`clearSession`,
 * `applySession`), so the `finally` only clears the slot if it still holds
 * this attempt — never a newer session's refresh.
 */
export function refreshAccessToken(): Promise<boolean> {
  if (inflightRefresh !== null) return inflightRefresh
  const attempt: Promise<boolean> = performRefresh().finally(() => {
    if (inflightRefresh === attempt) inflightRefresh = null
  })
  inflightRefresh = attempt
  return attempt
}

/**
 * Test seam: forget tokens, flags and the in-flight refresh — a fresh page
 * life, which has adopted no session yet. The cross-tab listener is removed and
 * installed again, so however many times a suite resets there is exactly one.
 */
export function resetApiState(): void {
  stepUp.reset()
  accessToken.value = null
  accessTokenExpiresAt.value = null
  tenantSuspended.value = false
  passwordChangeRequired.value = false
  sessionExpired.value = false
  tenantSlug.value = null
  sessionGeneration += 1
  inflightRefresh = null
  adoptedSessionId = null
  adoptedRefreshToken = null
  writeStored(REFRESH_TOKEN_KEY, null)
  writeStored(TENANT_SLUG_KEY, null)
  stopCrossTabSync()
  startCrossTabSync()
}

/**
 * Test seam: stop following other tabs' changes to the shared refresh token,
 * for a spec that needs to prove what happens when a storage event never
 * arrives. `resetApiState()` turns it back on.
 */
export function pauseCrossTabSync(): void {
  stopCrossTabSync()
}

/** Remember the slug the user signed in with, so the login form can prefill it. */
export function rememberTenantSlug(slug: string): void {
  tenantSlug.value = slug
  writeStored(TENANT_SLUG_KEY, slug)
}

/**
 * Forget the remembered workspace, on an explicit sign-out.
 *
 * The prefill is a convenience for the person who just left, and a disclosure
 * to whoever sits down next — which tenant this device was last signed in to.
 * Removing it is the boring safe value: an empty workspace field, exactly what
 * a device nobody has used shows. (Unlike the principal marker in the auth
 * store, nothing DECIDES anything on this value's absence, so no tombstone is
 * needed.) A session that merely expires keeps it: that is the same person,
 * coming back.
 */
export function forgetTenantSlug(): void {
  tenantSlug.value = null
  writeStored(TENANT_SLUG_KEY, null)
}
